import { LocalDate } from "@js-joda/core";
import { useState } from "react";
import {
  addTipDatesByKey,
  addUniqueTipDates,
  mergeTips,
  removeTip,
  removeTips,
  saveTipAmount,
  sortTipsByDate,
} from "../logic/tips.v2.logic";
import type {
  TipCrudActions,
  UseTipCrudActionsProps,
} from "../tips.component.types";

export const useTipCrudActions = (
  props: UseTipCrudActionsProps,
): TipCrudActions => {
  const [duplicateDateKeys, setDuplicateDateKeys] = useState<readonly string[]>(
    [],
  );

  const closeDuplicateDatesModal = (): void => {
    setDuplicateDateKeys([]);
  };

  const confirmAddDuplicateDates = async (): Promise<void> => {
    if (props.companyId === null || duplicateDateKeys.length === 0) {
      closeDuplicateDatesModal();
      return;
    }

    props.setIsLoadingTips(true);
    props.setErrorMessage(null);

    try {
      const result = await addTipDatesByKey({
        companyId: props.companyId,
        dateKeys: duplicateDateKeys,
      });

      if (result.ok) {
        props.setTips((currentTips) =>
          mergeTips({ currentTips, nextTips: result.value }),
        );
        closeDuplicateDatesModal();
        return;
      }

      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      props.setIsLoadingTips(false);
    }
  };

  const handleSelectedDatesChange = async (
    dates: readonly LocalDate[],
  ): Promise<void> => {
    props.setSelectedDates(dates);
    if (props.companyId === null) return;

    props.setIsLoadingTips(true);
    props.setErrorMessage(null);

    try {
      const result = await addUniqueTipDates({
        companyId: props.companyId,
        selectedDates: dates,
      });

      if (result.ok) {
        props.setTips((currentTips) =>
          mergeTips({ currentTips, nextTips: result.value.tips }),
        );
        if (dates[0]) {
          props.setCurrentDate(dates[0].withDayOfMonth(1));
        }
        props.setSelectedDates([]);
        if (result.value.skippedDates.length > 0) {
          setDuplicateDateKeys(result.value.skippedDates);
        }
      } else {
        props.setErrorMessage(props.t("Tips.UnexpectedError"));
      }
    } catch (_error: unknown) {
      props.setErrorMessage(props.t("Tips.UnexpectedError"));
    } finally {
      props.setIsLoadingTips(false);
    }
  };

  const handleDeleteTip = async (companyTipId: number): Promise<void> => {
    if (props.companyId === null) return;

    const result = await removeTip({
      companyId: props.companyId,
      companyTipId,
    });

    if (result.ok) {
      props.setTips((currentTips) =>
        currentTips.filter((tip) => tip.companyTipId !== companyTipId),
      );
      return;
    }

    props.setErrorMessage(props.t("Tips.UnexpectedError"));
  };

  const handleDeleteTips = async (
    companyTipIds: readonly number[],
  ): Promise<void> => {
    if (props.companyId === null || companyTipIds.length === 0) return;

    const result = await removeTips({
      companyId: props.companyId,
      companyTipIds,
    });

    if (result.ok) {
      const deletedTipIds = new Set(companyTipIds);
      props.setTips((currentTips) =>
        currentTips.filter((tip) => !deletedTipIds.has(tip.companyTipId)),
      );
      return;
    }

    props.setErrorMessage(props.t("Tips.UnexpectedError"));
  };

  const handleUpdateTipAmount = async (
    companyTipId: number,
    amount: number,
  ): Promise<void> => {
    if (props.companyId === null) return;

    const result = await saveTipAmount({
      amount,
      companyId: props.companyId,
      companyTipId,
    });

    if (result.ok) {
      props.setTips((currentTips) =>
        sortTipsByDate(
          currentTips.map((tip) =>
            tip.companyTipId === companyTipId ? result.value : tip,
          ),
        ),
      );
      return;
    }

    props.setErrorMessage(props.t("Tips.UnexpectedError"));
  };

  return {
    closeDuplicateDatesModal,
    confirmAddDuplicateDates,
    duplicateDateKeys,
    handleDeleteTip,
    handleDeleteTips,
    handleSelectedDatesChange,
    handleUpdateTipAmount,
  };
};
